import { useEffect, useRef, type PointerEvent } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import { useBoardViewportRef } from '../../viewport/BoardViewportContext';
import { deleteConnectionsByCardId } from '../../connections/connectionDeletion';
import { positionRelativeToDrop, type PositionUpdate } from '../../positioning/autoAlign';
import { getClusterDropTarget } from '../../positioning/clusterDrop';
import { updateCardOrClusterPosition } from '../../positioning/persistence';
import type { SortedBoardZIndexes } from '../../positioning/types';
import { sortBoardZIndexes } from '../../positioning/zIndex';
import { isClusterCard } from '../../selectors/boardCardSelectors';
import { moveShapeAttributes } from '../../shapes/geometry/shapeGeometry';
import {
  useBoardDragPreviewStore,
  useBoardElementRectStore,
  useBoardInteractionStore,
  useBoardSelectionStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWallStore,
  useWorkspaceUiStore,
  type CardDragPreview,
  type WallBoard,
  type WallCard,
  type WallShape,
} from '@walls/store';
import {
  getMovableSelectedElements,
  type MovableSelectedCard,
  type MovableSelectedShape,
} from '../utils/selectedCards';

interface UseCardDragOptions {
  board: WallBoard;
  canMove: boolean;
  card: WallCard;
  isClusterChild: boolean;
}

interface DragState {
  didDrag: boolean;
  draggedCards: MovableSelectedCard[];
  draggedShapes: MovableSelectedShape[];
  pointerId: number;
  pointerStartX: number;
  pointerStartY: number;
  promotedZIndexes: SortedBoardZIndexes | null;
}

interface CardDragPointerEvent {
  clientX: number;
  clientY: number;
  pointerId: number;
  preventDefault: () => void;
  stopPropagation: () => void;
}

interface DragMoveEventData {
  clientX: number;
  clientY: number;
  pointerId: number;
}

const dragThreshold = 3;
const clickSuppressionMs = 250;

export function useCardDrag({ board, canMove, card, isClusterChild }: UseCardDragOptions) {
  const dragState = useRef<DragState | null>(null);
  const dragMoveAnimationFrameRef = useRef<number | null>(null);
  const pendingDragMoveRef = useRef<DragMoveEventData | null>(null);
  const captureTargetRef = useRef<HTMLElement | null>(null);
  const stopWindowTrackingRef = useRef<(() => void) | null>(null);
  const suppressClickUntilRef = useRef(0);
  const cards = useWallContentStore((state) => state.cards);
  const measuredRects = useBoardElementRectStore((state) => state.rects);
  const moveCardToCluster = useWallContentStore((state) => state.moveCardToCluster);
  const shapes = useWallContentStore((state) => state.shapes);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const updateCardZIndexes = useWallContentStore((state) => state.updateCardZIndexes);
  const updateShape = useWallContentStore((state) => state.updateShape);
  const updateShapeZIndexes = useWallContentStore((state) => state.updateShapeZIndexes);
  const clearCardDragPreviews = useBoardDragPreviewStore(
    (state) => state.clearCardDragPreviews
  );
  const clearClusterPlacement = useBoardDragPreviewStore((state) => state.clearClusterPlacement);
  const setCardDragPreviews = useBoardDragPreviewStore((state) => state.setCardDragPreviews);
  const setClusterPlacement = useBoardDragPreviewStore((state) => state.setClusterPlacement);
  const setDragging = useBoardInteractionStore((state) => state.setDragging);
  const selectedIds = useBoardSelectionStore((state) => state.selectedIds);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const topicKey = useWallStore((state) => state.topicKey);
  const viewportRef = useBoardViewportRef();
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const canDrag =
    canMove &&
    !isClusterChild &&
    !card.isLocked &&
    !['hand', 'shape', 'line', 'text', 'cluster'].includes(selectedTool);

  const stopWindowTracking = () => {
    stopWindowTrackingRef.current?.();
    stopWindowTrackingRef.current = null;
  };

  const releasePointerCapture = (pointerId: number) => {
    const captureTarget = captureTargetRef.current;

    if (captureTarget?.hasPointerCapture(pointerId)) {
      captureTarget.releasePointerCapture(pointerId);
    }

    captureTargetRef.current = null;
  };

  const cancelScheduledDragMove = () => {
    if (dragMoveAnimationFrameRef.current != null) {
      cancelAnimationFrame(dragMoveAnimationFrameRef.current);
      dragMoveAnimationFrameRef.current = null;
    }

    pendingDragMoveRef.current = null;
  };

  const flushScheduledDragMove = () => {
    const pendingMove = pendingDragMoveRef.current;

    if (!pendingMove) {
      return;
    }

    if (dragMoveAnimationFrameRef.current != null) {
      cancelAnimationFrame(dragMoveAnimationFrameRef.current);
      dragMoveAnimationFrameRef.current = null;
    }

    pendingDragMoveRef.current = null;
    performDragMove(pendingMove);
  };

  const scheduleDragMove = (event: DragMoveEventData) => {
    pendingDragMoveRef.current = event;

    if (dragMoveAnimationFrameRef.current != null) {
      return;
    }

    dragMoveAnimationFrameRef.current = requestAnimationFrame(() => {
      dragMoveAnimationFrameRef.current = null;
      const pendingMove = pendingDragMoveRef.current;
      pendingDragMoveRef.current = null;

      if (pendingMove) {
        performDragMove(pendingMove);
      }
    });
  };

  const handlePointerMove = (event: CardDragPointerEvent) => {
    const currentDrag = dragState.current;

    if (!currentDrag || !canDrag || currentDrag.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = (event.clientX - currentDrag.pointerStartX) / zoom;
    const deltaY = (event.clientY - currentDrag.pointerStartY) / zoom;

    if (
      !currentDrag.didDrag &&
      Math.abs(deltaX) < dragThreshold &&
      Math.abs(deltaY) < dragThreshold
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    scheduleDragMove({
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
    });
  };

  const performDragMove = (event: DragMoveEventData) => {
    const currentDrag = dragState.current;

    if (!currentDrag || !canDrag || currentDrag.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = (event.clientX - currentDrag.pointerStartX) / zoom;
    const deltaY = (event.clientY - currentDrag.pointerStartY) / zoom;

    if (!currentDrag.didDrag) {
      currentDrag.promotedZIndexes = sortBoardZIndexes(cards, shapes, [
        ...currentDrag.draggedCards.map((draggedCard) => draggedCard.card.id),
        ...currentDrag.draggedShapes.map((draggedShape) => draggedShape.shape.id),
      ]);
      updateCardZIndexes(currentDrag.promotedZIndexes.cards);
      updateShapeZIndexes(currentDrag.promotedZIndexes.shapes);
    }

    currentDrag.didDrag = true;

    setCardDragPreviews(getCardDragPreviews(currentDrag.draggedCards, deltaX, deltaY));

    getShapeAttributeUpdates(currentDrag.draggedShapes, deltaX, deltaY).forEach(
      ({ attributes, shapeId }) => updateShape(shapeId, { attributes })
    );

    const clusterDrop =
      currentDrag.draggedShapes.length === 0
        ? getClusterDropTarget({
            board,
            cards,
            currentParticipantId,
            deltaX,
            deltaY,
            draggedCards: currentDrag.draggedCards,
            measuredRects,
            pointerPosition: getBoardPointerPosition(
              event,
              viewportRef?.current ?? null,
              zoom
            ),
          })
        : null;

    setClusterPlacement(
      clusterDrop && !clusterDrop.createCluster
        ? {
            clusterId: clusterDrop.cluster.id,
            draggedCardId: clusterDrop.card.id,
            placeholderBeforeChildId: clusterDrop.placeholderBeforeChildId,
            size: clusterDrop.size,
          }
        : null
    );
  };

  const handlePointerUp = (event: CardDragPointerEvent) => {
    const currentDrag = dragState.current;

    if (!currentDrag || currentDrag.pointerId !== event.pointerId) {
      return;
    }

    flushScheduledDragMove();
    dragState.current = null;
    currentDrag.draggedCards.forEach((draggedCard) => setDragging(draggedCard.card.id, false));
    currentDrag.draggedShapes.forEach((draggedShape) => setDragging(draggedShape.shape.id, false));
    stopWindowTracking();
    clearClusterPlacement();
    releasePointerCapture(currentDrag.pointerId);

    if (!currentDrag.didDrag) {
      clearCardDragPreviews();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    suppressClickUntilRef.current = Date.now() + clickSuppressionMs;

    const deltaX = (event.clientX - currentDrag.pointerStartX) / zoom;
    const deltaY = (event.clientY - currentDrag.pointerStartY) / zoom;

    const clusterDrop =
      currentDrag.draggedShapes.length === 0
        ? getClusterDropTarget({
            board,
            cards,
            currentParticipantId,
            deltaX,
            deltaY,
            draggedCards: currentDrag.draggedCards,
            measuredRects,
            pointerPosition: getBoardPointerPosition(
              event,
              viewportRef?.current ?? null,
              zoom
            ),
          })
        : null;

    if (clusterDrop) {
      moveCardToCluster(clusterDrop.card.id, clusterDrop.cluster.id, clusterDrop.zPos);

      void deleteConnectionsByCardId(board.id, clusterDrop.card.id, topicKey);

      if (isClusterCard(clusterDrop.card)) {
        void wallsApi.addClusterToCluster(
          board.id,
          clusterDrop.cluster.id,
          clusterDrop.card.id,
          clusterDrop.cluster.collapsed,
          clusterDrop.zPos,
          topicKey
        );
        publishBoardSocketPackage({
          action: 'addClusterToCluster',
          topic: board.id,
          updateDetail: {
            sourceClusterId: clusterDrop.card.id,
            targetClusterId: clusterDrop.cluster.id,
            zPos: clusterDrop.zPos,
          },
        });
      } else {
        void wallsApi.addCardToCluster(
          board.id,
          clusterDrop.cluster.id,
          clusterDrop.card.id,
          clusterDrop.zPos,
          topicKey
        );
        publishBoardSocketPackage({
          action: 'addCardToCluster',
          topic: board.id,
          updateDetail: {
            cardId: clusterDrop.card.id,
            clusterId: clusterDrop.cluster.id,
            zPos: clusterDrop.zPos,
          },
        });
      }

      clearCardDragPreviews();
      return;
    }

    const updates = getDragPositionUpdates({
      autoAlignCards: board.autoAlignCards,
      cards,
      deltaX,
      deltaY,
      draggedCards: currentDrag.draggedCards,
      draggedShapes: currentDrag.draggedShapes,
    });
    const shapeUpdates = getShapeAttributeUpdates(
      currentDrag.draggedShapes,
      deltaX,
      deltaY,
      currentDrag.promotedZIndexes
    );

    updates.forEach(({ cardId, xPos, yPos }) => {
      updateCard(cardId, { xPos, yPos });
      void updateCardOrClusterPosition(board.id, cards, cardId, xPos, yPos, topicKey);
      const movedCard = findCard(cards, cardId);
      publishBoardSocketPackage({
        action:
          movedCard && isClusterCard(movedCard) ? 'updateClusterPosition' : 'updateCardPosition',
        topic: board.id,
        position: { id: cardId, xPos, yPos },
      });
    });

    shapeUpdates.forEach(({ attributes, shape, zPos }) => {
      updateShape(shape.id, { attributes });
      void wallsApi.updateShape(board.id, shape.id, shape.type, attributes, zPos, topicKey);
      publishBoardSocketPackage({
        action: 'updateShape',
        topic: board.id,
        shape: { ...shape, attributes, zPos },
      });
    });

    if (currentDrag.promotedZIndexes) {
      void publishZIndexUpdates(board.id, currentDrag.promotedZIndexes, topicKey);
    }

    clearCardDragPreviews();
  };

  const handlePointerCancel = (pointerId?: number) => {
    const currentDrag = dragState.current;

    if (!currentDrag || (pointerId != null && currentDrag.pointerId !== pointerId)) {
      return;
    }

    dragState.current = null;
    currentDrag.draggedCards.forEach((draggedCard) => setDragging(draggedCard.card.id, false));
    currentDrag.draggedShapes.forEach((draggedShape) => setDragging(draggedShape.shape.id, false));
    cancelScheduledDragMove();
    stopWindowTracking();
    clearCardDragPreviews();
    clearClusterPlacement();
    releasePointerCapture(currentDrag.pointerId);
  };

  const handleWindowPointerMove = (event: globalThis.PointerEvent) => {
    handlePointerMove(event);
  };

  const handleWindowPointerUp = (event: globalThis.PointerEvent) => {
    handlePointerUp(event);
  };

  const handleWindowPointerCancel = (event: globalThis.PointerEvent) => {
    handlePointerCancel(event.pointerId);
  };

  const startWindowTracking = () => {
    stopWindowTracking();
    window.addEventListener('pointermove', handleWindowPointerMove, true);
    window.addEventListener('pointerup', handleWindowPointerUp, true);
    window.addEventListener('pointercancel', handleWindowPointerCancel, true);
    stopWindowTrackingRef.current = () => {
      window.removeEventListener('pointermove', handleWindowPointerMove, true);
      window.removeEventListener('pointerup', handleWindowPointerUp, true);
      window.removeEventListener('pointercancel', handleWindowPointerCancel, true);
    };
  };

  useEffect(
    () => () => {
      const currentDrag = dragState.current;
      if (currentDrag) {
        currentDrag.draggedCards.forEach((draggedCard) => setDragging(draggedCard.card.id, false));
        currentDrag.draggedShapes.forEach((draggedShape) =>
          setDragging(draggedShape.shape.id, false)
        );
      }
      cancelScheduledDragMove();
      clearCardDragPreviews();
      stopWindowTracking();
    },
    [clearCardDragPreviews, setDragging]
  );

  return {
    isDragging: Boolean(dragState.current?.didDrag),
    shouldSuppressClick: () => Date.now() < suppressClickUntilRef.current,
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (!canDrag || event.button !== 0 || card.xPos == null || card.yPos == null) {
        return;
      }

      clearClusterPlacement();
      clearCardDragPreviews();

      const draggedElements = getMovableSelectedElements({
        board,
        cards,
        currentParticipantId,
        pressedElementId: card.id,
        selectedIds,
        shapes,
        visibleParticipantIds,
      });

      if (draggedElements.cards.length === 0 && draggedElements.shapes.length === 0) {
        return;
      }

      const nextDrag = {
        didDrag: false,
        draggedCards: draggedElements.cards,
        draggedShapes: draggedElements.shapes,
        pointerId: event.pointerId,
        pointerStartX: event.clientX,
        pointerStartY: event.clientY,
        promotedZIndexes: null,
      };
      dragState.current = nextDrag;
      nextDrag.draggedCards.forEach((draggedCard) => setDragging(draggedCard.card.id, true));
      nextDrag.draggedShapes.forEach((draggedShape) => setDragging(draggedShape.shape.id, true));
      captureTargetRef.current = event.currentTarget;
      event.currentTarget.setPointerCapture(event.pointerId);
      startWindowTracking();
    },
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: (event: PointerEvent<HTMLElement>) => handlePointerCancel(event.pointerId),
  };
}

async function publishZIndexUpdates(
  boardId: string,
  zIndexes: SortedBoardZIndexes,
  topicKey: string | null
) {
  await Promise.all([
    zIndexes.cards.length > 0
      ? wallsApi.updateCardZIndexes(boardId, zIndexes.cards, topicKey)
      : Promise.resolve(),
    zIndexes.shapes.length > 0
      ? wallsApi.updateShapeZIndexes(boardId, zIndexes.shapes, topicKey)
      : Promise.resolve(),
  ]);
}

function getDragPositionUpdates({
  autoAlignCards,
  cards,
  deltaX,
  deltaY,
  draggedCards,
  draggedShapes,
}: {
  autoAlignCards: boolean;
  cards: WallCard[];
  deltaX: number;
  deltaY: number;
  draggedCards: MovableSelectedCard[];
  draggedShapes: MovableSelectedShape[];
}): PositionUpdate[] {
  const rawUpdates = draggedCards.map((draggedCard) => ({
    cardId: draggedCard.card.id,
    xPos: Math.round(draggedCard.startX + deltaX),
    yPos: Math.round(draggedCard.startY + deltaY),
  }));

  if (!autoAlignCards || draggedCards.length !== 1 || draggedShapes.length > 0) {
    return rawUpdates;
  }

  const [draggedCard] = draggedCards;
  const [rawUpdate] = rawUpdates;
  const droppedCard = {
    ...draggedCard.card,
    xPos: rawUpdate.xPos,
    yPos: rawUpdate.yPos,
  };
  const projectedCards = cards.map((card) => (card.id === droppedCard.id ? droppedCard : card));
  const autoAlignUpdates = positionRelativeToDrop(projectedCards, droppedCard);

  return autoAlignUpdates.length > 0 ? autoAlignUpdates : rawUpdates;
}

function getCardDragPreviews(
  draggedCards: MovableSelectedCard[],
  deltaX: number,
  deltaY: number
): Record<string, CardDragPreview> {
  return draggedCards.reduce<Record<string, CardDragPreview>>((previews, draggedCard) => {
    previews[draggedCard.card.id] = {
      deltaX: Math.round(draggedCard.startX + deltaX) - draggedCard.startX,
      deltaY: Math.round(draggedCard.startY + deltaY) - draggedCard.startY,
    };

    return previews;
  }, {});
}

function getShapeAttributeUpdates(
  draggedShapes: MovableSelectedShape[],
  deltaX: number,
  deltaY: number,
  promotedZIndexes?: SortedBoardZIndexes | null
) {
  return draggedShapes.map(({ shape, startAttributes }) => {
    const startShape: WallShape = {
      ...shape,
      attributes: startAttributes,
    };
    const attributesPatch = moveShapeAttributes(startShape, deltaX, deltaY);

    return {
      attributes: {
        ...startAttributes,
        ...attributesPatch,
      },
      shape,
      shapeId: shape.id,
      zPos:
        promotedZIndexes?.shapes.find((zIndex) => zIndex.shapeId === shape.id)?.zPos ??
        shape.zPos ??
        1,
    };
  });
}

function findCard(cards: WallCard[], cardId: string): WallCard | null {
  for (const card of cards) {
    if (card.id === cardId) {
      return card;
    }

    const childCard = findCard(card.cards, cardId);
    if (childCard) {
      return childCard;
    }
  }

  return null;
}

function getBoardPointerPosition(
  event: Pick<CardDragPointerEvent, 'clientX' | 'clientY'>,
  viewport: HTMLElement | null,
  zoom: number
) {
  if (!viewport) {
    return {
      x: event.clientX / zoom,
      y: event.clientY / zoom,
    };
  }

  const rect = viewport.getBoundingClientRect();

  return {
    x: viewport.scrollLeft + (event.clientX - rect.left) / zoom,
    y: viewport.scrollTop + (event.clientY - rect.top) / zoom,
  };
}
